import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Card, Button } from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/actions";

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{7,13}$/;
const TOAST_POSITION = toast.POSITION.TOP_CENTER;
const STORAGE_KEY = "loginObj";

const InputWithIcon = ({ id, type, onChange, placeholder }) => {
  const [showPassword, setShowPassword] = useState(false);

  const inputType = useMemo(() => {
    if (type !== "password") return type;
    return showPassword ? "text" : "password";
  }, [type, showPassword]);

  const handleTogglePassword = useCallback(() => {
    setShowPassword(prevState => !prevState);
  }, []);

  return (
    <div className="relative">
      <input
        id={id}
        type={inputType}
        onChange={onChange}
        placeholder={placeholder}
        required
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none pr-12"
      />
      {type === "password" && (
        <button
          type="button"
          onClick={handleTogglePassword}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-600 hover:text-gray-800"
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </button>
      )}
    </div>
  );
};

InputWithIcon.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string
};

InputWithIcon.defaultProps = {
  placeholder: ""
};

const ChangePassword = ({ onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    oldPass: "",
    newPass: "",
    confirmPass: ""
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const loginUser = useMemo(() => {
    try {
      const storedUser = localStorage.getItem(STORAGE_KEY);
      return storedUser ? JSON.parse(storedUser) : {};
    } catch {
      return {};
    }
  }, []);

  const userCode = useMemo(() => loginUser.user?.code || "", [loginUser]);
  const userEmail = useMemo(() => loginUser.user?.email || "", [loginUser]);
  const userMobile = useMemo(() => loginUser.user?.mobile || "", [loginUser]);

  const passwordCriteria = useCallback((password) => {
    if (!password) return false;
    return PASSWORD_REGEX.test(password);
  }, []);

  const showErrorToast = useCallback((message) => {
    toast.error(message, { position: TOAST_POSITION });
  }, []);

  const validateForm = useCallback(() => {
    const { oldPass, newPass, confirmPass } = formData;

    if (!oldPass.trim()) {
      showErrorToast("Please enter Old password.");
      return false;
    }
    if (!passwordCriteria(oldPass)) {
      showErrorToast(
        "Old Password must contain 7–13 characters with uppercase, lowercase, number and special character."
      );
      return false;
    }
    if (!newPass.trim()) {
      showErrorToast("Please enter new password.");
      return false;
    }
    if (!passwordCriteria(newPass)) {
      showErrorToast(
        "New Password must contain 7–13 characters with uppercase, lowercase, number and special character."
      );
      return false;
    }
    if (!confirmPass.trim()) {
      showErrorToast("Please enter confirm Password.");
      return false;
    }
    if (newPass !== confirmPass) {
      showErrorToast("Password and Confirm Password do not match");
      return false;
    }
    return true;
  }, [formData, passwordCriteria, showErrorToast]);

  const handleInputChange = useCallback((field) => (event) => {
    setFormData(prevData => ({
      ...prevData,
      [field]: event.target.value
    }));
  }, []);

  const handleUpdate = useCallback(async () => {
    if (!validateForm()) return;

    const { oldPass, confirmPass: newPassword } = formData;

    const payload = {
      userCode,
      passwordVal: btoa(newPassword),
      currentPasswordVal: btoa(oldPass),
      email: userEmail,
      mobile: userMobile,
      status: 1,
      createdBy: userCode || "ADMIN",
      modifiedBy: userCode || "ADMIN"
    };

    setLoading(true);
    try {
      const response = await apiService.forgetChangePassword(payload);
      if (response?.data?.responseCode == 1) {
        toast.success(response?.data?.reponseMessage || "Password changed successfully");
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        toast.error(response?.data?.reponseMessage || "Failed to change password");
      }
    } catch {
      toast.error("Failed to change password");
    } finally {
      setLoading(false);
    }
  }, [formData, userCode, userEmail, userMobile, validateForm, navigate]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const hasMinLength = (pass) => pass?.length >= 7 && pass?.length <= 13;
  const hasUpperCase = (pass) => /[A-Z]/.test(pass);
  const hasLowerCase = (pass) => /[a-z]/.test(pass);
  const hasNumber = (pass) => /\d/.test(pass);
  const hasSpecialChar = (pass) => /[!@#$%^&*()_+]/.test(pass);

  return (
    <Card
      elevation={3}
      sx={{
        width: '480px',
        padding: '40px',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        margin: 'auto'
      }}
    >
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Change Your Password</h1>
        <p className="text-gray-500 mt-2">Please enter your old password and set a new one</p>
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Old Password */}
        <div>
          <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700 mb-2">
            Old Password
          </label>
          <div className="relative">
            <input
              id="oldPassword"
              type={showOldPassword ? 'text' : 'password'}
              value={formData.oldPass}
              onChange={handleInputChange("oldPass")}
              placeholder="Type Old Password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none pr-12"
            />
            <button
              type="button"
              onClick={() => setShowOldPassword(!showOldPassword)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-600 hover:text-gray-800"
            >
              {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
            New Password
          </label>
          <div className="relative">
            <input
              id="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={formData.newPass}
              onChange={handleInputChange("newPass")}
              placeholder="Type New Password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none pr-12"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-600 hover:text-gray-800"
            >
              {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPass}
              onChange={handleInputChange("confirmPass")}
              placeholder="Type Confirm Password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none pr-12"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-600 hover:text-gray-800"
            >
              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </button>
          </div>
        </div>

        {/* Password Criteria for New Password */}
        {formData.newPass && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
            <ul className="space-y-1">
              <li className={`text-xs ${hasMinLength(formData.newPass) ? 'text-green-600' : 'text-gray-500'}`}>
                ✓ 7-13 characters
              </li>
              <li className={`text-xs ${hasUpperCase(formData.newPass) ? 'text-green-600' : 'text-gray-500'}`}>
                ✓ At least one uppercase letter
              </li>
              <li className={`text-xs ${hasLowerCase(formData.newPass) ? 'text-green-600' : 'text-gray-500'}`}>
                ✓ At least one lowercase letter
              </li>
              <li className={`text-xs ${hasNumber(formData.newPass) ? 'text-green-600' : 'text-gray-500'}`}>
                ✓ At least one number
              </li>
              <li className={`text-xs ${hasSpecialChar(formData.newPass) ? 'text-green-600' : 'text-gray-500'}`}>
                ✓ At least one special character (!@#$%^&*()_+)
              </li>
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4">
          <Button
            fullWidth
            variant="outlined"
            onClick={handleCancel}
            sx={{
              py: 1.5,
              borderColor: '#d1d5db',
              color: '#374151',
              '&:hover': {
                borderColor: '#9ca3af',
                bgcolor: '#f9fafb'
              },
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              borderRadius: '8px'
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleUpdate}
            disabled={loading}
            sx={{
              py: 1.5,
              bgcolor: '#2563eb',
              '&:hover': { bgcolor: '#1d4ed8' },
              '&:disabled': { bgcolor: '#93c5fd' },
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              borderRadius: '8px'
            }}
          >
            {loading ? 'Updating...' : 'Change Password'}
          </Button>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          Secure password change • Make sure to remember your new password
        </p>
      </div>
    </Card>
  );
};

ChangePassword.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default ChangePassword;